import { isAcceptanceTest } from 'hub-http/helpers/env';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetAiAccessControls } from 'academy-common-lib/features/aiControl/aiAccessQueries';
import { VideoAssetTypename } from '../../../../__generated__/chirp/com/hubspot/academy/catalog/rpc/LessonChirpService';
import { useAsyncBulkGenerateQuizMeMutation, useAsyncBulkGetQuizMeQuery } from '../../../../__generated__/chirp/com/hubspot/academy/gen/ai/skills/rpc/QuizMeRpc.hooks';
import UsageTracker from '../../../utils/UsageTracker';
import { DIFFICULTY_LEVELS } from '../interactiveEducationConstants';
import { useAsyncGetLazyPollingQuizMe } from '../queries/quizMeQueries';
import { collectTaskIdsFromState, useQuizMeSession } from '../utils/quizMeUtils';
import { shuffleTaskAnswers } from '../utils/contentUtils';
import { useFullLessonChirp } from '../../lessons/lessonQueries';
import { useFullShort } from '../../shortContent/shortContentQueries';
import { useAllVideosCaptionsExist } from '../../video/videoHooks';
import { handleApolloErrors } from '../../../utils/errorUtils';
export const useAsyncGetQuizMeFromCache = ({
  primaryContentId,
  contentType,
  lessonContentId,
  selectedDifficulty,
  skip = false
}) => {
  const shuffledCacheRef = useRef({
    correlationId: undefined,
    result: undefined
  });
  const isShorts = contentType === 'SHORT_FORM_CONTENT';
  const isLesson = contentType === 'LESSON';
  const {
    short
  } = useFullShort(primaryContentId, !isShorts || skip);
  const {
    lesson
  } = useFullLessonChirp({
    lessonId: Number(primaryContentId),
    skip: !isLesson || skip,
    fetchPolicy: 'cache-only'
  });
  const videoContents = useMemo(() => lesson === null || lesson === void 0 ? void 0 : lesson.content.filter(content => content.__typename === VideoAssetTypename), [lesson]);
  const contentIds = useMemo(() => {
    if (isLesson && videoContents) {
      return videoContents.map(content => Number(content.id));
    }
    if (isShorts) {
      return [Number(short === null || short === void 0 ? void 0 : short.reference)];
    }
    return [];
  }, [videoContents, short, isLesson, isShorts]);
  const {
    getQuizState
  } = useQuizMeSession();
  const taskIds = useMemo(() => {
    if (isLesson) {
      const hasDifficultyTasks = contentIds.some(id => {
        var _getQuizState;
        return !!((_getQuizState = getQuizState(id, contentType)) !== null && _getQuizState !== void 0 && _getQuizState.difficultyTasks);
      });
      if (hasDifficultyTasks) {
        return DIFFICULTY_LEVELS.flatMap(level => contentIds.flatMap(id => {
          var _getQuizState2;
          const taskId = (_getQuizState2 = getQuizState(id, contentType)) === null || _getQuizState2 === void 0 || (_getQuizState2 = _getQuizState2.difficultyTasks) === null || _getQuizState2 === void 0 ? void 0 : _getQuizState2[level];
          return taskId ? [taskId] : [];
        }));
      }
      return contentIds.flatMap(id => collectTaskIdsFromState(getQuizState(id, contentType)));
    }
    if (isShorts) return collectTaskIdsFromState(getQuizState(Number(primaryContentId), contentType));
    return [];
  }, [contentIds, contentType, getQuizState, isLesson, isShorts, primaryContentId]);
  const {
    data
  } = useAsyncBulkGetQuizMeQuery({
    variables: {
      taskIds
    },
    fetchPolicy: 'cache-only',
    skip: isAcceptanceTest() || taskIds.length === 0 || skip
  });
  const quizMeData = useMemo(() => {
    var _task;
    let task;
    if (isLesson) {
      var _state$tasks, _state$difficultyTask, _state$selectedDiffic;
      const state = lessonContentId ? getQuizState(lessonContentId, contentType) : undefined;
      const contentTaskId = (_state$tasks = state === null || state === void 0 ? void 0 : state.tasks) !== null && _state$tasks !== void 0 ? _state$tasks : state === null || state === void 0 || (_state$difficultyTask = state.difficultyTasks) === null || _state$difficultyTask === void 0 ? void 0 : _state$difficultyTask[(_state$selectedDiffic = state === null || state === void 0 ? void 0 : state.selectedDifficulty) !== null && _state$selectedDiffic !== void 0 ? _state$selectedDiffic : 'BEGINNER'];
      const taskIndex = contentTaskId ? taskIds.indexOf(contentTaskId) : -1;
      task = taskIndex >= 0 ? data === null || data === void 0 ? void 0 : data.tasks[taskIndex] : undefined;
    } else if (isShorts) {
      // tasks are stored in DIFFICULTY_LEVELS order (BEGINNER=0, INTERMEDIATE=1, ADVANCED=2).
      // Falls back to INTERMEDIATE when no difficulty is selected.
      const difficultyIndex = selectedDifficulty !== undefined ? DIFFICULTY_LEVELS.indexOf(selectedDifficulty) : DIFFICULTY_LEVELS.indexOf('INTERMEDIATE');
      task = data === null || data === void 0 ? void 0 : data.tasks[difficultyIndex];
    }
    const correlationId = (_task = task) === null || _task === void 0 || (_task = _task.quizResponse) === null || _task === void 0 ? void 0 : _task.correlationId;
    const cached = shuffledCacheRef.current;
    if (!correlationId) {
      return task;
    }
    if (correlationId === cached.correlationId) {
      return cached.result;
    }
    const shuffled = shuffleTaskAnswers(task);
    shuffledCacheRef.current = {
      correlationId,
      result: shuffled
    };
    return shuffled;
  }, [data, taskIds, contentType, lessonContentId, isLesson, isShorts, getQuizState, selectedDifficulty]);
  return quizMeData;
};
const useGetShortsQuizMeRequestParams = content => {
  const {
    contentPortalId,
    outputLanguage,
    videoId
  } = useMemo(() => ({
    contentPortalId: content !== null && content !== void 0 && content.hubId ? content.hubId : undefined,
    outputLanguage: content === null || content === void 0 ? void 0 : content.language,
    videoId: content !== null && content !== void 0 && content.reference ? Number(content === null || content === void 0 ? void 0 : content.reference) : undefined
  }), [content]);
  return contentPortalId && outputLanguage && videoId ? [{
    contentPortalId,
    outputLanguage,
    videoId
  }] : undefined;
};
export const useGenerateAsyncQuizMe = ({
  primaryContentId,
  contentType,
  lessonContentId,
  interactiveContentTrackingProps,
  triggerGeneration = true
}) => {
  var _quizState$difficulty;
  const {
    aiAccessControls
  } = useGetAiAccessControls();
  const isGenerativeAiEnabled = !!(aiAccessControls !== null && aiAccessControls !== void 0 && aiAccessControls.generativeAiEnabled);
  const {
    getQuizState,
    initialiseQuiz,
    deleteQuizState,
    setDifficultyTasks
  } = useQuizMeSession();
  const quizState = getQuizState(lessonContentId || primaryContentId, contentType);
  const hasDifficultyTasks = Object.values((_quizState$difficulty = quizState === null || quizState === void 0 ? void 0 : quizState.difficultyTasks) !== null && _quizState$difficulty !== void 0 ? _quizState$difficulty : {}).some(Boolean);
  const generationFiredRef = useRef(false);
  const [generationLoading, setGenerationLoading] = useState(false);
  const isShorts = contentType === 'SHORT_FORM_CONTENT';
  const isLesson = contentType === 'LESSON';
  const {
    short
  } = useFullShort(primaryContentId, !isShorts);
  const {
    lesson
  } = useFullLessonChirp({
    lessonId: Number(primaryContentId),
    skip: !isLesson
  });
  const lessonVideoContentIds = useMemo(() => {
    var _lesson$content$filte, _lesson$content;
    if (!lesson) return [];
    return (_lesson$content$filte = (_lesson$content = lesson.content) === null || _lesson$content === void 0 ? void 0 : _lesson$content.filter(content => content.__typename === VideoAssetTypename).map(content => Number(content.id))) !== null && _lesson$content$filte !== void 0 ? _lesson$content$filte : [];
  }, [lesson]);
  const shortsQuizMeRequestParams = useGetShortsQuizMeRequestParams(short);
  const {
    captionsExistMap
  } = useAllVideosCaptionsExist({
    videoContentIds: lessonVideoContentIds
  });
  const lessonQuizMeRequestParams = useMemo(() => {
    if (!captionsExistMap || !lesson) return undefined;
    const params = lesson.content.filter(content => content.__typename === VideoAssetTypename && captionsExistMap[String(content.id)] === true && !!content.reference && !!content.hubId).map(content => ({
      contentPortalId: content.hubId,
      videoId: Number(content.reference),
      outputLanguage: content.language,
      lessonContentId: Number(content.id)
    }));
    return params.length > 0 ? params : undefined;
  }, [captionsExistMap, lesson]);
  const contentIds = useMemo(() => {
    if (isLesson) {
      return (lessonQuizMeRequestParams === null || lessonQuizMeRequestParams === void 0 ? void 0 : lessonQuizMeRequestParams.map(request => request === null || request === void 0 ? void 0 : request.lessonContentId).filter(id => id !== undefined)) || [];
    }
    if (isShorts && primaryContentId) {
      return [primaryContentId];
    }
    return [];
  }, [isLesson, isShorts, lessonQuizMeRequestParams, primaryContentId]);
  const {
    getQuizMeLazy,
    quizMeError,
    quizMeLoading: pollingLoading,
    queryLoading: pollingQueryLoading,
    retryGeneration
  } = useAsyncGetLazyPollingQuizMe({
    contentIds,
    contentType
  });
  const [generateDifficultyQuizMe] = useAsyncBulkGenerateQuizMeMutation();
  const quizMeRequestParams = useMemo(() => shortsQuizMeRequestParams || lessonQuizMeRequestParams, [shortsQuizMeRequestParams, lessonQuizMeRequestParams]);
  const handleTriggerDifficultyGeneration = useCallback(() => {
    const alreadyFired = generationFiredRef.current;
    if (!quizMeRequestParams || isAcceptanceTest() || alreadyFired) return;
    generationFiredRef.current = true;
    setGenerationLoading(true);
    if (isLesson && lessonQuizMeRequestParams) {
      lessonQuizMeRequestParams.forEach(request => {
        if (request !== null && request !== void 0 && request.lessonContentId) initialiseQuiz({
          contentType,
          contentId: request === null || request === void 0 ? void 0 : request.lessonContentId
        });
      });
    }
    if (isShorts) {
      initialiseQuiz({
        contentType,
        contentId: primaryContentId
      });
    }

    // Single call: each param is expanded into one entry per DIFFICULTY_LEVELS.
    const videoRequests = DIFFICULTY_LEVELS.flatMap(difficulty => quizMeRequestParams.map(param => Object.assign({}, param, {
      difficulty
    })));
    generateDifficultyQuizMe({
      variables: {
        videos: videoRequests
      }
    }).then(result => {
      var _result$data$result$t, _result$data;
      const tasks = (_result$data$result$t = (_result$data = result.data) === null || _result$data === void 0 ? void 0 : _result$data.result.tasks) !== null && _result$data$result$t !== void 0 ? _result$data$result$t : [];
      tasks.forEach((task, index) => {
        const {
          difficulty,
          lessonContentId: paramLessonContentId
        } = videoRequests[index];
        const contentId = paramLessonContentId !== null && paramLessonContentId !== void 0 ? paramLessonContentId : primaryContentId;
        setDifficultyTasks({
          contentType,
          contentId,
          difficultyTasks: {
            [difficulty]: task.taskId
          }
        });
      });

      // Tasks are in DIFFICULTY_LEVELS order, matching collectTaskIdsFromState.
      const allTaskIds = tasks.map(task => task.taskId);
      if (allTaskIds.length > 0) {
        void getQuizMeLazy({
          variables: {
            taskIds: allTaskIds
          }
        });
      }
      if (interactiveContentTrackingProps) {
        UsageTracker.trackStandalone('Learning Center Interaction', Object.assign({}, interactiveContentTrackingProps, {
          action: 'ai-quiz-me-generated-quiz'
        }));
      }
    }).catch(err => {
      handleApolloErrors({
        error: err,
        loggingTitle: 'learningCenter.interactiveEducation.errors.asyncQuizGenerationError'
      });
    }).finally(() => {
      setGenerationLoading(false);
    });
  }, [contentType, generateDifficultyQuizMe, getQuizMeLazy, initialiseQuiz, interactiveContentTrackingProps, isLesson, isShorts, lessonQuizMeRequestParams, primaryContentId, quizMeRequestParams, setDifficultyTasks]);

  // Resets guard ref and clears all quiz state so generation re-fires on remount.
  const onAsyncRegenerateQuiz = useCallback(() => {
    generationFiredRef.current = false;
    deleteQuizState({
      contentType,
      contentId: lessonContentId || primaryContentId
    });
    handleTriggerDifficultyGeneration();
  }, [contentType, deleteQuizState, handleTriggerDifficultyGeneration, lessonContentId, primaryContentId]);
  const shouldTriggerQuizGeneration = triggerGeneration && !generationLoading && !hasDifficultyTasks;
  useEffect(() => {
    if ((shouldTriggerQuizGeneration || retryGeneration) && isGenerativeAiEnabled) {
      handleTriggerDifficultyGeneration();
    }
  }, [handleTriggerDifficultyGeneration, isGenerativeAiEnabled, retryGeneration, shouldTriggerQuizGeneration]);
  return {
    onAsyncRegenerateQuiz,
    generateQuizError: quizMeError,
    generateQuizLoading: generationLoading || pollingLoading || pollingQueryLoading
  };
};